import type { Services } from "../data/services";

/**
 * Port of `FroshBotContextAssembler.swift`.
 *
 * The persona and the tool protocol live server-side. What the server cannot
 * know is this student's courses, grades, matches and calendar — that snapshot
 * is app-only, so it rides in on the first turn of a conversation.
 */
export async function assembleContext(services: Services): Promise<string> {
  const { academic, matching, reward, challenge, messaging, feed, auth } = services;

  const [
    user,
    courses,
    assignments,
    gpa,
    matches,
    balance,
    todayChallenge,
    convos,
    events,
    activities,
  ] = await Promise.all([
    auth.getCurrentUser(),
    academic.getCourses().catch(() => []),
    academic.getAssignments().catch(() => []),
    academic.getOverallGPA().catch(() => undefined),
    matching.getMatches(0, 5).catch(() => []),
    reward.getBalance().catch(() => undefined),
    challenge.getTodayChallenge().catch(() => undefined),
    messaging.getConversations().catch(() => []),
    feed.getPostsByType("event", 0, 10).catch(() => []),
    feed.getPostsByType("activity", 0, 10).catch(() => []),
  ]);

  const sections: string[] = [];

  let profile = "## Student Profile\n";
  profile += `- Name: ${user.displayName}\n`;
  if (user.school) profile += `- School: ${user.school}\n`;
  if (user.major) profile += `- Major: ${user.major}\n`;
  if (user.year) profile += `- Year: ${user.year}\n`;
  if (gpa !== undefined) profile += `- GPA: ${gpa.toFixed(2)}\n`;
  profile += `- Frosh Points: ${user.froshPoints}`;
  if (balance) profile += ` (rank #${balance.rank ?? 0} global)`;
  profile += "\n";
  if (user.vibeAnalysisText) profile += `- Vibe: ${user.vibeAnalysisText}\n`;
  if (user.zodiacSign) profile += `- Zodiac: ${user.zodiacSign}\n`;
  sections.push(profile);

  if (courses.length > 0) {
    let s = "## Courses\n";
    for (const c of courses) {
      const grade = c.currentGrade !== undefined ? `${c.currentGrade.toFixed(1)}%` : "N/A";
      s += `- ${c.code ?? c.id}: ${c.name} (${grade})`;
      if (c.professor) s += ` — Prof. ${c.professor.displayName}`;
      if (c.schedule) s += ` [${c.schedule}]`;
      if (c.officeHours) s += ` OH: ${c.officeHours}`;
      s += "\n";
    }
    sections.push(s);
  }

  const upcoming = assignments.filter((a) => a.status === "upcoming" || a.status === "inProgress");
  if (upcoming.length > 0) {
    let s = "## Upcoming Assignments\n";
    for (const a of upcoming.slice(0, 7)) {
      const weight = a.weight !== undefined ? `${Math.round(a.weight * 100)}% weight` : "";
      s += `- ${a.title} — due ${a.dueDate} [${a.status}] ${weight}\n`;
    }
    sections.push(s);
  }

  if (matches.length > 0) {
    let s = "## Top Matches\n";
    for (const m of matches) {
      s += `- ${m.user.displayName}: ${Math.round(m.overallScore * 100)}% match\n`;
    }
    sections.push(s);
  }

  const rsvpd = events.filter((e) => e.isRSVPd === true);
  const joined = activities.filter((a) => a.isJoinedByMe === true);
  if (rsvpd.length > 0 || joined.length > 0) {
    let s = "## Activity\n";
    for (const e of rsvpd) s += `- RSVP'd: ${e.content}\n`;
    for (const a of joined) s += `- Joined: ${a.content}\n`;
    sections.push(s);
  }

  const unread = convos.filter((c) => c.unreadCount > 0);
  if (unread.length > 0) {
    let s = "## Unread Messages\n";
    for (const c of unread.slice(0, 5)) {
      const name = c.isGroup
        ? (c.groupName ?? "Group")
        : (c.participants[0]?.displayName ?? "Unknown");
      s += `- ${name}: ${c.unreadCount} unread\n`;
    }
    sections.push(s);
  }

  if (todayChallenge) {
    sections.push(
      `## Today's Challenge\n- ${todayChallenge.prompt} (${todayChallenge.submissionCount} submissions)\n`,
    );
  }

  const now = new Date();
  sections.push(
    `## Current Date\n${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`,
  );

  return sections.join("\n");
}
